<template>
  <div
    v-if="data.assignedCoachLoaded"
    class="i-cart"
  >
    <iBackground
      :background-image="bgGradient"
      image-position="center center"
    />
    <iReportNavbar
      :core-items="navigationItemsData.coreItems"
      :profile-items="navigationItemsData.profileItems"
      :support-items="navigationItemsData.supportItems"
    />

    <div>
      <iReserveBooking
        :booking="data.upcomingUnpaidCoaching || undefined"
        :checkout-trigerred="data.bookingTriggered"
        @fetch-booking="fetchUpcomingUnpaidCoachingBooking"
        @update-countdown="data.countdownTimer = $event"
        @trigger-checkout="data.checkoutTriggered = true"
        @revert-triggers="revertTriggers"
      />
      <iScopingDetails
        :title="COACHING_PLAN.title"
        :price="COACHING_PLAN.price"
        :countdown-timer="data.countdownTimer"
      />
      <iCoachSummary />
    </div>

    <iSelectPlanFooter
      :coaching-plans="{ [StripePrice.COACHING_DISCOVERY_ONE]: COACHING_PLAN }"
      :chosen-plan="StripePrice.COACHING_DISCOVERY_ONE"
      :disable-payment-until-booking="!data.upcomingUnpaidCoaching"
      :checkout-trigerred="data.checkoutTriggered"
      @trigger-booking="data.bookingTriggered = true"
    />

    <iBaseModal
      visible
      class="zigpoll-modal"
      :class="{ visible: data.zigpollModalVisible }"
      @click-on-close="data.zigpollModalVisible = false"
    >
      <div class="zigpoll-modal-wrap">
        <div class="zigpoll-modal-text">You can close this modal now!</div>
        <div id="zigpoll-embed-target"></div>
      </div>
    </iBaseModal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive } from "vue"
import { useCoachingStore } from "@/stores/coaching"
import { useReportStore } from "@/stores/report"
import { breakpointLg } from "@/styles/variables"

import onAwayChangeDocumentTitle from "@/composables/onAwayChangeDocumentTitle"
import navigationItems from "@/composables/navigation"
import { GlobalVariables } from "@/helpers/globalVariables"
import api from "@/services/apiClient"
import GtmService, { UserFlow } from "@/services/gtmService"

// COMPONENTS
import iBaseModal from "@/components/base/BaseModal.vue"
import iCoachSummary from "@/components/coaching/selectPlan/CoachSummary.vue"
import iReserveBooking from "@/components/coaching/selectPlan/ReserveBooking.vue"
import iSelectPlanFooter from "@/components/coaching/selectPlan/SelectPlanFooter.vue"
import iScopingDetails from "@/components/coaching/selectPlan/ScopingDetails.vue"
import iReportNavbar from "@/components/report/navbar/ReportNavbar.vue"
import iBackground from "@/components/global/Background.vue"

// ASSETS
import bgGradient from "@/assets/confirm/bg-gradient-multi.png"

// TYPES
import ReportStatus from "@/shared/enums/ReportStatus"
import StripePrice from "@/shared/enums/StripePrice"
import CalcomBooking from "@/shared/types/CalcomBooking"

// STORES
const coachingStore = useCoachingStore()
const reportStore = useReportStore()

// CONSTANTS
const COACHING_PLAN = {
  id: StripePrice.COACHING_DISCOVERY_ONE,
  isSubscription: false,
  stripePrice: StripePrice.COACHING_DISCOVERY_ONE,
  upgradeTier: ReportStatus.PREMIUM,
  details: [],
  price: "149.90",
  title: "Discovery Session"
}

// DATA
const data = reactive({
  assignedCoachLoaded: false,
  zigpollModalVisible: false,
  zigpollModalDelayMs: 45000, // 45 seconds
  countdownTimer: "",
  upcomingUnpaidCoaching: null as CalcomBooking | null,
  checkoutTriggered: false,
  bookingTriggered: false
})

// FUNCTIONS
/**
 * Send a GTM event when the user views the coaching plans
 */
const sendGTMEvent = async () => {
  try {
    const response = await api.stripe.getPrices([StripePrice.COACHING_DISCOVERY_ONE])
    const pricesArr = response.prices

    if (!!pricesArr[0].uuid && pricesArr[0].uuid === COACHING_PLAN.stripePrice) {
      await GtmService.viewItemListEvent(UserFlow.COACHING, [
        {
          id: pricesArr[0].productId,
          price: pricesArr[0].price / 100,
          quantity: 1,
          name: pricesArr[0].name
        }
      ])
    }
  } catch (error) {
    console.error("Failed to load prices", error)
  }
}

const revertTriggers = () => {
  data.bookingTriggered = false
  data.checkoutTriggered = false
}
// COMPUTED
const navigationItemsData = computed(() => navigationItems())

// FUNCTIONS
const fetchUpcomingUnpaidCoachingBooking = async () => {
  try {
    const upcomingBooking = await api.booking.getUpcoming({
      bookingEventTypes: ["discovery"],
      isPaid: false
    })
    data.upcomingUnpaidCoaching = upcomingBooking.booking
  } catch (error) {
    console.error("Error fetching user's upcoming unpaid coachng booking:", error)
  }
}

const loadZigpoll = (delayMs: number) => {
  const isMobileView = window.innerWidth <= breakpointLg

  if (isMobileView) {
    if (window.Zigpoll) window.Zigpoll.refresh()
    reportStore.initMobileZigpollPrepayment(delayMs, GlobalVariables.zigpolls.finishedCoaching).then((modalVisible) => {
      data.zigpollModalVisible = modalVisible
    })
  } else {
    if (window.Zigpoll) reportStore.deactivateZigpollReload()
    reportStore.initDesktopZigpollPrepayment(delayMs)
  }
}

onAwayChangeDocumentTitle()

// LIFECYCLE HOOKS
onMounted(async () => {
  loadZigpoll(data.zigpollModalDelayMs)

  await fetchUpcomingUnpaidCoachingBooking()

  // We need to get the assigned coach again, because it will have changed after the user has booked a session
  await coachingStore.loadAssignedCoach()
  data.assignedCoachLoaded = true

  // Send a GTM event when the user views the coaching plans
  sendGTMEvent()
})
</script>

<style lang="scss" scoped>
.i-cart {
  .zigpoll-modal {
    visibility: hidden;

    &.visible {
      visibility: visible;
    }

    &-wrap {
      display: flex;
      flex-direction: column;
      min-height: 90px;
      max-height: 60vh;
      overflow: auto;
      position: relative;
    }

    &-text {
      font-weight: bold;
      position: absolute;
      text-align: center;
      top: 50%;
      width: 100%;
    }
  }
}
</style>
